import { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import {
  setFetcherSettingsDetailsData,
  setIsDetailsLoading,
} from "../../../../features/fetcherSettingsSlice";
import { FetcherSettingsEndPointLogic } from "../../../../logic/DataCollection/FetcherSettings/FetcherSettingsEndPointLogic";
import ReportObjectsPanel from "./ReportObjectsPanel";
import ViewReportSkeleton from "./ViewReportSkeleton";
import {
  parseDuplicateOf,
  rowId,
  selectCatalogueRows,
  selectDetailObjects,
} from "../helpers/fetcherCatalogue";
import {
  FETCHER_SETTINGS_DETAILS_QUERY_ID,
  getFirstWebsiteSharedUid,
} from "../helpers/fetcherSettingsQueryIds";
import { formatDisplayDateTime } from "../helpers/formatters";

interface Props {
  open: boolean;
  reportId: string;
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (reportId: string) => void;
  onShowBrands: (reportId: string) => void;
  /** opens the twin named in the possible-duplicate banner */
  onOpenTwin: (reportId: string) => void;
  onCreateInterface: () => void;
  canEdit: boolean;
}

const SECTION_LABEL_SX = {
  fontSize: "11px",
  letterSpacing: "0.5px",
  textTransform: "uppercase" as const,
  color: "#00000099",
  fontWeight: 700,
};

/** One "<count> <label>" cell of the strip under the title. */
const StripCount = ({
  value,
  label,
  onClick,
}: {
  value: number;
  label: string;
  onClick?: () => void;
}) => (
  <Stack direction="row" alignItems="baseline" spacing={0.75}>
    <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>{value}</Typography>
    {onClick && value > 0 ? (
      <Link
        component="button"
        underline="always"
        color="secondary"
        sx={{ fontSize: "12px" }}
        onClick={onClick}
      >
        {label} - view list
      </Link>
    ) : (
      <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
        {label}
      </Typography>
    )}
  </Stack>
);

/** A disabled select, so the field keeps its chevron while read only. */
const ReadOnlySelect = ({ label, value }: { label: string; value: string }) => (
  <TextField
    select
    fullWidth
    required
    size="small"
    label={label}
    value={value}
    disabled
    color="secondary"
  >
    <MenuItem value={value}>{value}</MenuItem>
  </TextField>
);

/**
 * The read-only report detail. Identity comes straight off the catalogue row;
 * the objects panel below it is the same component the editor uses, rendered
 * read only.
 */
const ViewReportModal = ({
  open,
  reportId,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
  onOpenTwin,
  onCreateInterface,
  canEdit,
}: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const websiteSharedUid = getFirstWebsiteSharedUid(userData);

  const {
    fetcherSettingsMainTablesData,
    fetcherSettingsDetailsData,
    isDetailsLoading,
  } = useSelector((state: AppState) => state.fetcherSettings);
  const reportRows = useMemo(
    () => selectCatalogueRows(fetcherSettingsMainTablesData, "report"),
    [fetcherSettingsMainTablesData],
  );

  const report = useMemo(
    () => reportRows.find((row) => rowId(row.item_id) === reportId),
    [reportRows, reportId],
  );

  /**
   * On a report `duplicate_of` is advisory and never blocks a save. Each id is
   * resolved to its name so the banner can link to the twin.
   */
  const twins = useMemo(() => {
    if (!report) return [];
    return parseDuplicateOf(report.duplicate_of).map((id) => ({
      id,
      name:
        reportRows.find((row) => rowId(row.item_id) === id)?.item_name ?? "",
    }));
  }, [report, reportRows]);

  useEffect(() => {
    if (!open || !reportId || !oauthTokenHeader || !websiteSharedUid) return;
    const loadDetails = async () => {
      try {
        dispatch(setIsDetailsLoading(true));
        dispatch(setFetcherSettingsDetailsData(null));
        await FetcherSettingsEndPointLogic.getFetcherSettingsDetailsData({
          oauthTokenHeader,
          queryId: FETCHER_SETTINGS_DETAILS_QUERY_ID,
          websiteSharedUids: [websiteSharedUid],
          item_id: reportId,
          level: "report",
        });
      } catch (e: any) {
        console.error("Error loading report details:", e);
      } finally {
        dispatch(setIsDetailsLoading(false));
      }
    };
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportId]);

  const interfaceId = rowId(report?.interface_id ?? null);

  // the detail query already splits the object rows by scope
  const inheritedObjects = useMemo(
    () => selectDetailObjects(fetcherSettingsDetailsData, "inherited"),
    [fetcherSettingsDetailsData],
  );
  const ownObjects = useMemo(
    () => selectDetailObjects(fetcherSettingsDetailsData, "own"),
    [fetcherSettingsDetailsData],
  );

  if (!report) return null;

  const usedByBrands = report.used_by_brands ?? 0;
  const interfaceLabel = report.interface_id
    ? `${report.interface_name ?? "-"} (${interfaceId})`
    : "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "1000px", maxWidth: "96vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>View Report</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        {report.item_name} ({rowId(report.item_id)}) &middot; modified{" "}
        {formatDisplayDateTime(report.modified)}
      </Typography>
      <Divider />

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: "#FAFAFC",
          borderBottom: "1px solid #0000001F",
        }}
      >
        <StripCount
          value={usedByBrands}
          label="brands"
          onClick={() => onShowBrands(rowId(report.item_id))}
        />
        <Divider orientation="vertical" flexItem />
        <StripCount value={report.child_configs ?? 0} label="configs" />
        <Divider orientation="vertical" flexItem />
        <StripCount value={report.own_objects ?? 0} label="objects" />
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            Interface
          </Typography>
          <Chip
            size="small"
            color="secondary"
            label={report.interface_name ?? "-"}
          />
        </Stack>
      </Stack>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {isDetailsLoading ? (
          <ViewReportSkeleton />
        ) : (
          <>
            {twins.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Possible duplicate.</b>{" "}
                {twins.map((twin, index) => (
                  <Box component="span" key={twin.id}>
                    {index > 0 && ", "}
                    <Link
                      component="button"
                      underline="always"
                      color="inherit"
                      sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
                      onClick={() => onOpenTwin(twin.id)}
                    >
                      {twin.name ? `${twin.name} (${twin.id})` : twin.id}
                    </Link>
                  </Box>
                ))}{" "}
                {twins.length === 1 ? "matches" : "match"} on interface,
                collection type and the name with a copy suffix removed.
                Advisory only - it never blocks a save.
              </Alert>
            )}

            <Stack spacing={2.5}>
              <Typography sx={SECTION_LABEL_SX}>Identity</Typography>

              <TextField
                fullWidth
                required
                size="small"
                label="Report Name"
                value={report.item_name ?? ""}
                disabled
                color="secondary"
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      sx={{ flex: 1 }}
                      select
                      required
                      size="small"
                      label="Interface"
                      value={interfaceLabel}
                      disabled
                      color="secondary"
                    >
                      <MenuItem value={interfaceLabel}>
                        {interfaceLabel}
                      </MenuItem>
                    </TextField>
                    <Button
                      color="secondary"
                      sx={{ whiteSpace: "nowrap", mt: 0.25 }}
                      onClick={onCreateInterface}
                      disabled
                    >
                      Create New Interface
                    </Button>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ReadOnlySelect
                    label="Collection Type"
                    value={report.collection_type ?? ""}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                size="small"
                label="URI Value"
                placeholder="optional"
                value={report.uri_value ?? ""}
                disabled
                color="secondary"
              />
            </Stack>

            <ReportObjectsPanel
              inheritedLabel={`Inherited from Interface - ${report.interface_name ?? "-"} (${interfaceId})`}
              inheritedRows={inheritedObjects}
              ownLabel="This report"
              ownRows={ownObjects}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Chip size="small" label="Read only" />
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <>
            <Button
              color="secondary"
              onClick={() => onDuplicate(rowId(report.item_id))}
              disabled
            >
              Duplicate
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSwitchToEdit}
              disabled
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewReportModal;
